import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Context de autenticação
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Componentes de autenticação
import Auth from './pages/Auth';
import { ProtectedRoute } from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';

// Componentes antigos (manteremos temporariamente)
import Navbar from './components/Navbar';
import Home from './pages/Home';
import CriarCurso from './pages/CriarCurso';
import DetalheCurso from './pages/DetalheCurso';
import AssistirCurso from './pages/AssistirCurso';
import CriarVideo from './pages/CriarVideo';
import MeusCursos from './pages/MeusCursos';
import MeusCertificados from './pages/MeusCertificados';
import ValidarCertificado from './pages/ValidarCertificado';
import CursosPorCategoria from './pages/CursosPorCategoria';

// Novo componente principal
import { MainLayout } from './components/MainLayout';

// Componentes Admin
import AdminLayout from './pages/admin/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import GerenciarUsuarios from './pages/admin/GerenciarUsuarios';
import GerenciarCursos from './pages/admin/GerenciarCursos';
import EditarCurso from './pages/admin/EditarCurso';
import GerenciarTemplates from './pages/admin/GerenciarTemplates';
import GerenciarInstrutores from './pages/admin/GerenciarInstrutores';
import GerenciarEmpresas from './pages/admin/GerenciarEmpresas';
import GerenciarCategorias from './pages/admin/GerenciarCategorias';
import GerenciarVideos from './pages/admin/GerenciarVideos';
import Estatisticas from './pages/admin/Estatisticas';

// Componente de Impersonificação
import ImpersonatePage from './pages/ImpersonatePage';

gsap.registerPlugin(ScrollTrigger);

// Componente principal da aplicação
function AppContent() {
  const containerRef = useRef(null);
  const { isAuthenticated, loading, isAdmin } = useAuth();

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Verificar se elementos existem antes de animar
      const fadeInElements = containerRef.current?.querySelectorAll(".fade-in")
      if (fadeInElements && fadeInElements.length > 0) {
        gsap.fromTo(
          fadeInElements,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.1,
            ease: "power2.out",
          },
        );
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  // Mostrar loading enquanto verifica autenticação
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Carregando...</p>
        </div>
      </div>
    );
  }

  // Se não está autenticado, mostrar página de login
  if (!isAuthenticated()) {
    return <Auth />;
  }

  // Se está autenticado, mostrar aplicação principal
  return (
    <div ref={containerRef} className="App min-h-screen">
      <Routes>
        {/* Rotas Admin */}
        <Route path="/admin" element={
          <AdminRoute>
            <AdminLayout>
              <Dashboard />
            </AdminLayout>
          </AdminRoute>
        } />
        <Route path="/admin/estatisticas" element={
          <AdminRoute>
            <AdminLayout>
              <Estatisticas />
            </AdminLayout>
          </AdminRoute>
        } />
        <Route path="/admin/usuarios" element={
          <AdminRoute>
            <AdminLayout>
              <GerenciarUsuarios />
            </AdminLayout>
          </AdminRoute>
        } />
        <Route path="/admin/cursos" element={
          <AdminRoute>
            <AdminLayout>
              <GerenciarCursos />
            </AdminLayout>
          </AdminRoute>
        } />
        <Route path="/admin/cursos/:id/editar" element={
          <AdminRoute>
            <AdminLayout>
              <EditarCurso />
            </AdminLayout>
          </AdminRoute>
        } />
        <Route path="/admin/templates" element={
          <AdminRoute>
            <AdminLayout>
              <GerenciarTemplates />
            </AdminLayout>
          </AdminRoute>
        } />
        <Route path="/admin/instrutores" element={
          <AdminRoute>
            <AdminLayout>
              <GerenciarInstrutores />
            </AdminLayout>
          </AdminRoute>
        } />
        <Route path="/admin/empresas" element={
          <AdminRoute>
            <AdminLayout>
              <GerenciarEmpresas />
            </AdminLayout>
          </AdminRoute>
        } />
        <Route path="/admin/categorias" element={
          <AdminRoute>
            <AdminLayout>
              <GerenciarCategorias />
            </AdminLayout>
          </AdminRoute>
        } />
        <Route path="/admin/videos" element={
          <AdminRoute>
            <AdminLayout>
              <GerenciarVideos />
            </AdminLayout>
          </AdminRoute>
        } />
        
        {/* Rota de Impersonificação (não protegida) */}
        <Route path="/impersonate" element={<ImpersonatePage />} />
        
        {/* Rota principal protegida */}
        <Route path="/" element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        } />
        
        {/* Rotas antigas protegidas */}
        <Route path="/old" element={
          <ProtectedRoute>
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
              <Navbar />
              <div className="pt-4">
                <Home />
              </div>
            </div>
          </ProtectedRoute>
        } />
        <Route path="/criar-curso" element={
          <AdminRoute>
            <CriarCurso />
          </AdminRoute>
        } />
        <Route path="/curso/:id" element={
          <ProtectedRoute>
            <DetalheCurso />
          </ProtectedRoute>
        } />
        <Route path="/categoria/:categoriaId" element={
          <ProtectedRoute>
            <MainLayout>
              <CursosPorCategoria />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/assistir-curso/:id" element={
          <ProtectedRoute>
            <AssistirCurso />
          </ProtectedRoute>
        } />
        <Route path="/meus-cursos" element={
          <ProtectedRoute>
            <MeusCursos />
          </ProtectedRoute>
        } />
        <Route path="/meus-certificados" element={
          <ProtectedRoute>
            <MeusCertificados />
          </ProtectedRoute>
        } />
        <Route path="/certificados/:credentialId" element={
          <ValidarCertificado />
        } />
        <Route path="/curso/:id/criar-video" element={
          <AdminRoute>
            <CriarVideo />
          </AdminRoute>
        } />
        <Route path="/criar-video" element={
          <AdminRoute>
            <CriarVideo />
          </AdminRoute>
        } />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }}>
      <AuthProvider>
        <div className="App">
          <AppContent />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App; 